import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { EditIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { RangeValue } from '@react-types/shared';
import { isEmpty, uniq, uniqBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import Permissioned from 'dashboard/components/Permissioned';
import ReactSelect from 'dashboard/components/ReactSelect';
import { FilterTagsCondition } from 'dashboard/components/TagsConditionFilter';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import FormAssignCase, { AssignCaseFormData } from 'dashboard/forms/FormAssignCase';
import FormBulkSetCaseState, { BulkSetCaseStateData } from 'dashboard/forms/FormBulkSetCaseState';
import {
  ModalBody,
  ModalContainer,
  ModalDescription,
  ModalHeader,
} from 'dashboard/layouts/ModalLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Account } from 'dashboard/models/account';
import { Case, CaseStatus } from 'dashboard/models/case';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseState, CaseTemplateBasicsOption } from 'dashboard/models/case-template';
import {
  AttributeColumn,
  CasesFilterRelativeTimeSpan,
  CasesTableView,
  generateFilterFromRelativeTimeSpan,
} from 'dashboard/models/table-view';
import { Tag } from 'dashboard/models/tag';
import { Team } from 'dashboard/models/team';
import { User, UserStatus } from 'dashboard/models/user';
import { CasesFilterParams, FilterValues } from 'dashboard/redux/actions/cases';
import { Z_INDICES } from 'dashboard/styles/variables';
import CasesTable from 'dashboard/views/DashboardCasesView/ConfigurableCasesTable/CasesTable';
import CasesTableControls from 'dashboard/views/DashboardCasesView/ConfigurableCasesTable/CasesTableControls';
import FormRenameTableView from 'dashboard/views/DashboardCasesView/ConfigurableCasesTable/FormRenameTableView';
import FormSaveTableView from 'dashboard/views/DashboardCasesView/ConfigurableCasesTable/FormSaveTableView';
import TableViewConfigurationDropdown from 'dashboard/views/DashboardCasesView/TableViewConfigurationDropdown';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { FieldSchema, FieldSchemaType } from 'lib/fields';
import useConfirmation from 'lib/hooks/useConfirmation';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { Iso8601DateString } from 'lib/types';

type TableViewConfiguration = CasesTableView['attributes']['configuration'];

const TableTitleContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: ${Spacing[2]};
`;

const FiltersWrapper = styled('div')`
  display: grid;
  gap: ${Spacing[4]};
  margin-top: ${Spacing[4]};
`;

const ViewActionsBar = styled('div')`
  display: flex;
  gap: ${Spacing[2]};
`;

const SelectView = styled(ReactSelect<string>)`
  min-width: 300px;
`;

export interface BulkActionCaseInfo {
  caseId: string;
  status: CaseStatus;
  state: CaseState;
  caseTemplateId: string;
}

export const DEFAULT_COLUMN_CONFIGURATION = [
  {
    key: 'id',
    label: 'Case ID',
  },
  {
    key: 'assignee',
    label: 'Assignee',
  },
  {
    key: 'created_at',
    label: 'Created At',
  },
  {
    key: 'active_sla_expires_at',
    label: 'SLA',
  },
  {
    key: 'status',
    label: 'Status',
  },
];

export const HARDCODED_COLUMN_OPTIONS = [
  {
    key: 'id',
    label: 'Case ID',
  },
  {
    key: 'created_at',
    label: 'Created At',
  },
  {
    key: 'active_sla_expires_at',
    label: 'SLA',
  },
  {
    key: 'resolved_at',
    label: 'Resolved At',
  },
  {
    key: 'case_queue_name',
    label: 'Queue',
  },
  {
    key: 'account.full_name',
    label: 'Account Name',
  },
  {
    key: 'account.reference_id',
    label: 'Account Reference ID',
  },
  {
    key: 'assignee',
    label: 'Assignee',
  },
  {
    key: 'status',
    label: 'Status',
  },
  {
    key: 'tags',
    label: 'Tags',
  },
];

// NB: Only limit to string/number fields for now.
const SUPPORTED_FIELD_SCHEMA_TYPES = [FieldSchemaType.String, FieldSchemaType.Number];

export const generateTableViewConfiguration = (
  filters: CasesFilterParams,
  columns: AttributeColumn[],
  sortParams: SortParams,
  relativeTimeSpan: CasesFilterRelativeTimeSpan,
): TableViewConfiguration => {
  const configuration = {
    columns,
    filters: {
      'status-names': filters.caseStateNames === 'all' ? [] : filters.caseStateNames,
      'relative-time-span': relativeTimeSpan,
      'case-template-ids': filters.caseTemplateIds === 'all' ? [] : filters.caseTemplateIds,
      'case-queue-ids': filters.caseQueueIds === 'all' ? [] : filters.caseQueueIds,
      'team-assignee-ids': filters.teamAssigneeIds === 'all' ? [] : filters.teamAssigneeIds,
      'assignee-ids': filters.assigneeIds === 'all' ? [] : filters.assigneeIds,
      'tag-conditions': filters.tagsConditions,
    },
    sort: {
      sort: sortParams?.sort,
      'should-sort-descending': sortParams?.shouldSortDescending,
    },
  };

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return configuration;
};

export interface ConfigurableCasesTableProps {
  accounts: Account[];
  allTags: Tag[];
  cases: Case[];
  casesTags: Tag[];
  activeCaseTemplateBasicsOptions: CaseTemplateBasicsOption[];
  caseQueues: CaseQueue[];
  caseCaseQueues: CaseQueue[];
  caseUsers: User[];
  filters: CasesFilterParams;
  hasFilterOnlyOnOpenCases: boolean;
  hasUsedFilters: boolean;
  loadingCases: boolean;
  loadingCaseTemplateBasicsOptions: boolean;
  loadingTableViews: boolean;
  loadingTags: boolean;
  pageSize: number;
  paginationParams: PaginationParams;
  sortParams: SortParams;
  currentTableView: CasesTableView;
  activeTableViews: CasesTableView[];
  activeTeams: Team[];
  hasCasesBasic: boolean;
  hasCasesEnhanced: boolean;
  hasCasesEnterprise: boolean;
  users: User[];
  handleAssigneeFilterUpdate: (
    userAssigneeIds: FilterValues,
    teamAssigneeIds: FilterValues,
  ) => void;
  handleNotAssigneeFilterUpdate: (userAssigneeIds: string[]) => void;
  handleBulkAssign: (taskIds: string[], userId: string, message: string) => void;
  handleBulkSetState: (taskIds: string[], data: BulkSetCaseStateData) => void;
  handleCaseTemplateFilterUpdate: (templateIds: FilterValues) => void;
  handleCaseQueueFilterUpdate: (caseQueueIds: FilterValues) => void;
  handleClearFilters: () => void;
  handleDateTypeFilterUpdate: (dateType: 'created_at' | 'resolved_at') => void;
  handleDateUpdate: (range: { startDate?: Iso8601DateString; endDate?: Iso8601DateString }) => void;
  handleDateUpdateV2: (range: RangeValue<DateValue> | null) => void;
  handleOmniSearch: (omniQuery: string) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleCreateTableView: (name: string, configuration: TableViewConfiguration) => void;
  handleSaveTableView: (id: string, name: string, configuration: TableViewConfiguration) => void;
  handleArchiveTableView: (id: string) => void;
  handleSetCurrentTableView: (id: string) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  handleStateFilterUpdate: (stateIds: FilterValues) => void;
  handleTagsConditionsFilterUpdate: (tagsConditions: FilterTagsCondition[][]) => void;
}

// TODO: CAS-86 [chris] DO NOT COPY THIS COMPONENT. We're planning on refactoring this component to extract
// a more generalizable ConfigurableTable component which will have some nicer APIs for displaying columns for any kind of object.
const ConfigurableCasesTable = ({
  accounts,
  allTags,
  cases,
  casesTags,
  activeCaseTemplateBasicsOptions,
  caseUsers,
  caseQueues,
  caseCaseQueues,
  filters,
  hasFilterOnlyOnOpenCases,
  hasUsedFilters,
  loadingCases,
  loadingCaseTemplateBasicsOptions,
  loadingTableViews,
  loadingTags,
  pageSize,
  paginationParams,
  sortParams,
  currentTableView,
  activeTableViews,
  activeTeams,
  hasCasesBasic,
  hasCasesEnhanced,
  hasCasesEnterprise,
  users,
  handleAssigneeFilterUpdate,
  handleNotAssigneeFilterUpdate,
  handleBulkAssign,
  handleBulkSetState,
  handleCaseTemplateFilterUpdate,
  handleCaseQueueFilterUpdate,
  handleClearFilters,
  handleDateTypeFilterUpdate,
  handleDateUpdate,
  handleDateUpdateV2,
  handleOmniSearch,
  handlePageLinkClick,
  handlePageSizeClick,
  handleCreateTableView,
  handleSaveTableView,
  handleSetCurrentTableView,
  handleArchiveTableView,
  handleSortUpdate,
  handleStateFilterUpdate,
  handleTagsConditionsFilterUpdate,
}: ConfigurableCasesTableProps) => {
  const [selectedCasesInfo, setSelectedCasesInfo] = useState<BulkActionCaseInfo[]>([]);
  const [assignModal, { enable: showAssignModal, disable: hideAssignModal }] = useToggle(false);
  const [stateModal, { enable: showStateModal, disable: hideStateModal }] = useToggle(false);
  const [saveViewModal, { enable: showSaveViewModal, disable: hideSaveViewModal }] =
    useToggle(false);
  const [renameViewModal, { enable: showRenameViewModal, disable: hideRenameViewModal }] =
    useToggle(false);
  const [
    showArchiveTableViewModal,
    {
      prompt: promptArchiveTableViewModal,
      confirm: confirmArchiveTableView,
      cancel: cancelArchiveTableView,
    },
  ] = useConfirmation();

  const activeOrgUsers = useMemo(() => {
    return users ? users.filter((user: User) => user.attributes.status === UserStatus.Active) : [];
  }, [users]);

  const hiddenColumns: Record<string, boolean> = {
    active_sla_expires_at: !hasCasesEnhanced && !hasCasesEnterprise,
  };

  const filteredHardcodedColumnOptions = HARDCODED_COLUMN_OPTIONS.filter(
    (column) => !hiddenColumns[column.key],
  );
  const [columns, setColumns] = useState(filteredHardcodedColumnOptions);

  useEffect(() => {
    if (loadingTableViews && currentTableView) {
      setColumns(currentTableView.attributes.configuration.columns);
    }
  }, [loadingTableViews, currentTableView]);

  const viewOptions = useMemo(() => {
    return activeTableViews.map((tableView) => ({
      label: tableView.attributes.name,
      value: tableView.id,
    }));
  }, [activeTableViews]);

  const hasTableViewWritePermission = usePermission(Permission.TableViewWrite);
  const searchNextCasesBackendEnabled = useFeatureFlag('dashboard:search-next-backend-cases');

  const columnAttributeOptions = useMemo(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const caseTemplateFieldSchemas = activeCaseTemplateBasicsOptions.reduce((prev, current) => {
      const fieldSchemasToAdd = current['field-schema'].filter(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (fs: FieldSchema) => SUPPORTED_FIELD_SCHEMA_TYPES.includes(fs.type) && !isEmpty(fs.label),
      );
      return [...prev, ...fieldSchemasToAdd];
    }, []);

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const fieldSchemaOptions = caseTemplateFieldSchemas.map((fs: FieldSchema) => ({
      key: fs.key,
      label: fs.label,
    }));

    // Field schemas with the same key can show up in multiple case templates. When configuring the index
    // we want these duplicates to be collapsed into a single option.
    const dedupedFieldSchemaOptions = uniqBy('key', fieldSchemaOptions);
    return [...filteredHardcodedColumnOptions, ...dedupedFieldSchemaOptions];
  }, [activeCaseTemplateBasicsOptions, filteredHardcodedColumnOptions]);

  const selectedCaseTemplateBasicsOption = useMemo(() => {
    const selectedCaseTemplateIds = uniq(
      selectedCasesInfo.map((kaseInfo: BulkActionCaseInfo) => kaseInfo.caseTemplateId),
    );

    return selectedCaseTemplateIds.length === 1
      ? activeCaseTemplateBasicsOptions.find((option) => option.id === selectedCaseTemplateIds[0])
      : undefined;
  }, [activeCaseTemplateBasicsOptions, selectedCasesInfo]);

  const isSelectedOfSameTemplate = selectedCaseTemplateBasicsOption != null;

  const selectedCaseIds = useMemo(() => {
    return selectedCasesInfo.map((kaseInfo: BulkActionCaseInfo) => kaseInfo.caseId);
  }, [selectedCasesInfo]);

  const bulkHandleSetState = async (values: BulkSetCaseStateData) => {
    await handleBulkSetState(selectedCaseIds, values);
    setSelectedCasesInfo([]);
    hideStateModal();
  };

  const bulkHandleAssign = async (values: AssignCaseFormData) => {
    await handleBulkAssign(selectedCaseIds, values.userId, values.message);
    setSelectedCasesInfo([]);
    hideAssignModal();
  };

  const saveTableView = (
    name: string,
    relativeTimeSpan: CasesFilterRelativeTimeSpan,
    overwrite: boolean,
  ) => {
    const nextTableViewConfiguration = generateTableViewConfiguration(
      filters,
      columns,
      sortParams,
      relativeTimeSpan,
    );

    if (overwrite) {
      handleSaveTableView(currentTableView.id, name, nextTableViewConfiguration);
    } else {
      handleCreateTableView(name, nextTableViewConfiguration);
    }

    const { dateType, startDate, endDate } = generateFilterFromRelativeTimeSpan(relativeTimeSpan);
    handleDateTypeFilterUpdate(dateType ?? 'created_at');
    handleDateUpdate({
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      startDate: startDate ? DateTime.fromISO(startDate, { setZone: true }).toISO() : null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      endDate: endDate ? DateTime.fromISO(endDate, { setZone: true }).toISO() : null,
    });

    hideSaveViewModal();
  };

  const handleRenameTableView = ({ name }: { name: string }) => {
    handleSaveTableView(currentTableView.id, name, currentTableView.attributes.configuration);

    hideRenameViewModal();
  };

  const hasWritePermission = usePermission(Permission.CaseWrite);
  const hasBulkAssignPermission = usePermission(Permission.CaseBulkAssign);
  const hasBulkWritePermission = usePermission(Permission.CaseBulkWrite);

  const maySelectCases = hasWritePermission || hasBulkAssignPermission || hasBulkWritePermission;

  return (
    <>
      <Card>
        <Card.Body>
          <TableTitleContainer>
            <Card.Title>Cases</Card.Title>
            <ViewActionsBar>
              <Permissioned permission={Permission.TableViewView}>
                <SelectView
                  placeholder='Select a view'
                  onChange={(value: string) => {
                    const nextTableView = activeTableViews.find(
                      (tableView) => tableView.id === value,
                    );
                    handleSetCurrentTableView(value);
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    setColumns(nextTableView.attributes.configuration.columns);
                  }}
                  options={viewOptions}
                  value={currentTableView?.id}
                  portal
                />
              </Permissioned>
              <Button
                id='configurable-cases-table__clear'
                width='auto'
                variant='inverse'
                onClick={handleClearFilters}
              >
                Clear filters
              </Button>
              <Permissioned permission={Permission.TableViewWrite}>
                <Button
                  id='configurable-cases-table__save-view'
                  width='auto'
                  variant='inverse'
                  onClick={showSaveViewModal}
                >
                  Save view
                </Button>
              </Permissioned>
              <Permissioned permission={Permission.TableViewWrite}>
                <ContextMenu
                  contentProps={{ style: { zIndex: Z_INDICES.layeredDropdown } }}
                  variant='inverse'
                  side='bottom'
                  align='end'
                  items={[
                    {
                      id: 'rename',
                      onClick: () => showRenameViewModal(),
                      hidden: !currentTableView,
                      variant: 'basic' as const,
                      label: (
                        <>
                          <EditIcon />
                          &nbsp; Rename
                        </>
                      ),
                    },
                    {
                      id: 'archive',
                      onClick: () =>
                        promptArchiveTableViewModal(() =>
                          handleArchiveTableView(currentTableView.id),
                        ),
                      hidden: !currentTableView,
                      variant: 'danger',
                      label: (
                        <>
                          <TrashIcon />
                          &nbsp; Delete
                        </>
                      ),
                    },
                  ]}
                  renderItem={(item) => (
                    <ContextMenu.Item
                      id='configurable-cases-table__row--view-actions'
                      onClick={item.onClick}
                      variant={item.variant}
                    >
                      {item.label}
                    </ContextMenu.Item>
                  )}
                />
              </Permissioned>
            </ViewActionsBar>
          </TableTitleContainer>

          <FiltersWrapper>
            <CasesTableControls
              activeCaseTemplateBasicsOptions={activeCaseTemplateBasicsOptions}
              filters={filters}
              loadingCaseTemplateBasicsOptions={loadingCaseTemplateBasicsOptions}
              tags={allTags}
              activeTeams={activeTeams}
              caseQueues={caseQueues}
              users={users}
              handleAssigneeFilterUpdate={handleAssigneeFilterUpdate}
              handleNotAssigneeFilterUpdate={handleNotAssigneeFilterUpdate}
              handleCaseTemplateFilterUpdate={handleCaseTemplateFilterUpdate}
              handleCaseQueueFilterUpdate={handleCaseQueueFilterUpdate}
              handleDateTypeFilterUpdate={handleDateTypeFilterUpdate}
              handleDateUpdate={handleDateUpdateV2}
              handleStateFilterUpdate={handleStateFilterUpdate}
              handleTagsConditionsFilterUpdate={handleTagsConditionsFilterUpdate}
              handleOmniSearch={handleOmniSearch}
              searchNextCasesBackendEnabled={searchNextCasesBackendEnabled}
              tableActions={
                <>
                  <Permissioned permission={Permission.CaseBulkWrite}>
                    <Button
                      id='configurable-cases-table__bulk-set-status'
                      onClick={showStateModal}
                      variant='basic'
                      // Disable the button if some selected cases have different templates
                      disabled={selectedCasesInfo.length === 0 || !isSelectedOfSameTemplate}
                    >
                      Set Status ({selectedCasesInfo.length})
                    </Button>
                  </Permissioned>
                  {hasBulkAssignPermission && (
                    <Button
                      id='configurable-cases-table__bulk-assign'
                      onClick={showAssignModal}
                      variant='basic'
                      disabled={selectedCasesInfo.length === 0}
                    >
                      Assign ({selectedCasesInfo.length})
                    </Button>
                  )}
                </>
              }
              propertiesButton={
                (hasCasesBasic || hasCasesEnhanced || hasCasesEnterprise) && (
                  <TableViewConfigurationDropdown
                    disabled={!hasTableViewWritePermission}
                    disabledTooltipMessage='Your role does not have permission to configure the Cases view.'
                    columnConfiguration={columns}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    attributeOptions={columnAttributeOptions}
                    updateColumnConfiguration={setColumns}
                  />
                )
              }
            />
          </FiltersWrapper>
          <CasesTable
            accounts={accounts}
            columnOrdering={columns}
            cases={cases}
            caseUsers={caseUsers}
            caseCaseQueues={caseCaseQueues}
            tags={casesTags}
            sortParams={sortParams}
            hasUsedFilters={hasUsedFilters}
            hasFilterOnlyOnOpenCases={hasFilterOnlyOnOpenCases}
            isLoading={loadingCases || loadingTableViews || loadingTags}
            handleSortUpdate={handleSortUpdate}
            selectedCasesInfo={selectedCasesInfo}
            setSelectedCasesInfo={setSelectedCasesInfo}
            maySelectCases={maySelectCases}
          />
          {cases.length > 0 && (
            <Pagination
              pageSize={pageSize}
              paginationParams={paginationParams}
              handlePageSizeClick={handlePageSizeClick}
              handlePageLinkClick={handlePageLinkClick}
            />
          )}
        </Card.Body>
      </Card>
      <ModalWrapper isOpen={stateModal} handleCloseModal={hideStateModal}>
        <ModalContainer data-test='bulk-set-state-case-modal'>
          <ModalHeader data-test='bulk-set-state-case-modal__header' onClose={hideStateModal}>
            Set Status
          </ModalHeader>

          <ModalBody>
            <FormBulkSetCaseState
              handleCancel={hideStateModal}
              onSubmit={bulkHandleSetState}
              selectedStates={selectedCasesInfo.map((info) => info.state)}
              caseTemplateStates={selectedCaseTemplateBasicsOption?.states ?? []}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <ModalWrapper isOpen={assignModal} handleCloseModal={hideAssignModal}>
        <ModalContainer data-test='bulk-assign-case-modal'>
          <ModalHeader data-test='bulk-assign-case-modal__header' onClose={hideAssignModal}>
            Assign {selectedCasesInfo.length} {selectedCasesInfo.length === 1 ? 'Case' : 'Cases'}
          </ModalHeader>

          <ModalBody>
            <FormAssignCase
              handleCancel={hideAssignModal}
              onSubmit={bulkHandleAssign}
              users={activeOrgUsers}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <ModalWrapper isOpen={saveViewModal} handleCloseModal={hideSaveViewModal}>
        <ModalContainer>
          <ModalHeader onClose={hideSaveViewModal}>Save view</ModalHeader>
          <ModalDescription>
            This will save your current filters, columns, and sort.
          </ModalDescription>
          <ModalBody>
            <FormSaveTableView
              tableViewName={currentTableView ? currentTableView.attributes.name : 'Default view'}
              handleCancel={hideSaveViewModal}
              enableOverwrite={activeTableViews.length > 0}
              saveTableView={saveTableView}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <ModalWrapper isOpen={renameViewModal} handleCloseModal={hideRenameViewModal}>
        <ModalContainer>
          <ModalHeader onClose={hideRenameViewModal}>Rename view</ModalHeader>
          <ModalBody>
            <FormRenameTableView
              tableViewName={currentTableView ? currentTableView.attributes.name : 'Default view'}
              handleCancel={hideRenameViewModal}
              handleRenameTableView={handleRenameTableView}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <ModalWrapper isOpen={showArchiveTableViewModal} handleCloseModal={cancelArchiveTableView}>
        <ConfirmationModal
          cancelAction={cancelArchiveTableView}
          confirmAction={confirmArchiveTableView}
          header='Are you sure?'
          confirmActionText='Delete'
        >
          This action is permanent. Are you sure you want to delete&nbsp;
          {currentTableView?.attributes?.name}?
        </ConfirmationModal>
      </ModalWrapper>
    </>
  );
};

export default ConfigurableCasesTable;
